import { askAboutAndReveal } from "../../../state/chatActions";
import type { MerchantSummary, MonthlyRow } from "../../../data/types";
import { MCC_RAMP } from "../../../data/seasonality";
import { monthsToBreach } from "../../../data/simulation";
import { ProjectionChart } from "./ProjectionChart";
import { fmtEur, fmtPct, shortMonth } from "./format";
import styles from "./MerchantProfile.module.css";

interface MerchantProfileProps {
  merchant: MerchantSummary;
  monthly: MonthlyRow[];
}

export function MerchantProfile({ merchant, monthly }: MerchantProfileProps) {
  const rows = monthly.filter(
    (r) => r.merchant_id === merchant.id && r.active
  );
  const latest = rows[rows.length - 1];
  const ramp = MCC_RAMP[merchant.mcc] ?? 0.02;
  const mtb = monthsToBreach(latest?.vampRatio ?? merchant.fraudRatioPct, ramp);

  const statusClass =
    merchant.status === "Terminated"
      ? styles.critical
      : merchant.status === "Warning"
        ? styles.warn
        : styles.health;

  const ask = (prompt: string) => askAboutAndReveal(prompt);

  return (
    <div className={`${styles.card} ${statusClass}`}>
      <header className={styles.header}>
        <div className={styles.ident}>
          <span className={styles.name}>{merchant.name}</span>
          <span className={styles.id}>{merchant.id}</span>
        </div>
        <div className={styles.tags}>
          <button
            type="button"
            className={styles.tag}
            onClick={() =>
              ask(
                `which other merchants are in mcc ${merchant.mcc} (${merchant.mccDesc}) and how do their profiles compare with ${merchant.name}?`
              )
            }
            title={`compare other ${merchant.mcc} merchants`}
          >
            {merchant.mcc}
          </button>
          <span className={styles.tagMuted}>{merchant.mccDesc}</span>
        </div>
      </header>

      <div className={styles.kpis}>
        <Kpi
          label="vamp"
          value={fmtPct(merchant.fraudRatioPct)}
          tone="accent"
          onAsk={() =>
            ask(
              `explain the ${fmtPct(merchant.fraudRatioPct)} vamp on ${merchant.name} — what's the composition (tc40 vs tc15), and how has it changed over the last 3 months?`
            )
          }
        />
        <Kpi
          label="peak"
          value={fmtPct(merchant.peakVampRatio)}
          onAsk={() =>
            ask(
              `${merchant.name} peaked at ${fmtPct(merchant.peakVampRatio)} — when did that happen and what caused it?`
            )
          }
        />
        <Kpi
          label="refund"
          value={fmtPct(merchant.refundRatePct, 2)}
          onAsk={() =>
            ask(
              `${merchant.name}'s refund rate is ${fmtPct(merchant.refundRatePct, 2)}. is their cancellation path healthy? where would i expect that to go next?`
            )
          }
        />
        <Kpi
          label="tc score"
          value={String(merchant.tcScore)}
          onAsk={() =>
            ask(
              `what does a tc score of ${merchant.tcScore} mean for ${merchant.name}? is it recoverable and how?`
            )
          }
        />
        <Kpi
          label="months to breach"
          value={mtb >= 900 ? "—" : `${mtb}mo`}
          tone={mtb < 6 ? "warn" : "default"}
          onAsk={() =>
            ask(
              `${merchant.name} is ${mtb >= 900 ? "stable" : `~${mtb} months`} from the 1.5% visa ceiling at the current ramp. what interventions would extend that runway, and by how much?`
            )
          }
        />
        <Kpi
          label="monthly vol"
          value={fmtEur(latest?.volume ?? 0)}
          onAsk={() =>
            ask(
              `${merchant.name} runs ~${fmtEur(latest?.volume ?? 0)}/mo. how much revenue is at stake if they're terminated vs extended?`
            )
          }
        />
        <Kpi
          label="subs"
          value={merchant.subs.toLocaleString()}
          onAsk={() =>
            ask(
              `what does the subscriber trend look like for ${merchant.name} — are they growing, churning, or flat?`
            )
          }
        />
        <Kpi
          label="go-live"
          value={shortMonth(merchant.goLive)}
          onAsk={() =>
            ask(
              `${merchant.name} has been with us since ${merchant.goLive}. how does their trajectory compare with other merchants from the same wave?`
            )
          }
        />
      </div>

      <ProjectionChart merchant={merchant} monthly={rows} />

      <footer className={styles.footer}>
        <button
          type="button"
          className={styles.footerCell}
          onClick={() =>
            ask(
              `${merchant.name}'s refund policy is "${merchant.refundPolicy}". how does that correlate with their vamp and what would a better policy look like?`
            )
          }
        >
          <span className={styles.muted}>refund policy</span>
          <span>{merchant.refundPolicy}</span>
        </button>
        <button
          type="button"
          className={styles.footerCell}
          onClick={() =>
            ask(
              `their cancellation path is "${merchant.cancelEase}". is that driving disputes? what does a healthy cancel flow look like for this mcc?`
            )
          }
        >
          <span className={styles.muted}>cancel</span>
          <span>{merchant.cancelEase}</span>
        </button>
        <button
          type="button"
          className={styles.footerCell}
          onClick={() =>
            ask(
              `their chargeback multiplier is ${merchant.cbMult.toFixed(2)}×. what's baseline for mcc ${merchant.mcc} and how do we pull this down?`
            )
          }
        >
          <span className={styles.muted}>cb mult</span>
          <span>{merchant.cbMult.toFixed(2)}×</span>
        </button>
      </footer>
    </div>
  );
}

interface KpiProps {
  label: string;
  value: string;
  tone?: "default" | "accent" | "warn";
  onAsk?: () => void;
}
function Kpi({ label, value, tone = "default", onAsk }: KpiProps) {
  return (
    <button
      type="button"
      className={styles.kpi}
      onClick={onAsk}
      disabled={!onAsk}
      title={onAsk ? `ask about ${label}` : undefined}
    >
      <span className={styles.kpiLabel}>{label}</span>
      <span
        className={`${styles.kpiValue} ${tone === "accent" ? styles.kpiAccent : tone === "warn" ? styles.kpiWarn : ""}`}
      >
        {value}
      </span>
    </button>
  );
}
